import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomProducts, linkCustomProductIID, discontinueCustomProduct, verifyIIDAdmin } from "../../../redux/Thunk/Product/ProductThunks";
import Pagination from "../../common/Pagination";
import Loader from "../../Loader";

function Requests() {
  const {
    customProducts,
    loading,
  } = useSelector((state) => state?.product);

  const [currentPage, setCurrentPage] = useState(1);
  const [showIIDModal, setShowIIDModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [iidValue, setIidValue] = useState("");
  const [fetchedProduct, setFetchedProduct] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const limit = 12;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchCustomProducts({ page: currentPage, limit }));
  }, [dispatch, currentPage]);

  const handleNextPage = () => {
    if (currentPage < customProducts?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleOpenModal = (request) => {
    setSelectedRequest(request);
    setIidValue(request.IID || "");
    setFetchedProduct(null);
    setShowIIDModal(true);
  };

  const handleVerify = async () => {
    if (!iidValue.trim()) return;
    setIsVerifying(true);
    try {
      const product = await dispatch(verifyIIDAdmin(iidValue.trim())).unwrap();
      setFetchedProduct(product);
    } catch (err) {
      setFetchedProduct(null);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSaveIID = async () => {
    if (!iidValue.trim() || !fetchedProduct) return;
    await dispatch(linkCustomProductIID({ id: selectedRequest.id, IID: iidValue.trim() }));
    setShowIIDModal(false);
  };

  const handleToggleDiscontinue = async (item) => {
    const action = item.isDiscontinued ? 'activate' : 'discontinue';
    if (window.confirm(`Are you sure you want to ${action} this custom product?`)) {
      await dispatch(discontinueCustomProduct({ id: item.id, isDiscontinued: !item.isDiscontinued }));
    }
  };

  return (
    <section className="bg-gray-50 min-h-screen">
      <Loader loading={loading} />

      <div className="py-5 px-6 border-b border-gray-200 flex items-center justify-between bg-white shadow-sm sticky top-0 z-10">
        <div>
          <h3 className="text-gray-900 font-extrabold text-xl">Custom Product Dashboard</h3>
          <p className="text-gray-500 text-xs mt-0.5">Manage doctor-added products and inventory requests</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-primary-50 px-3 py-1.5 rounded-lg border border-primary-100">
            <span className="text-primary-700 font-bold text-sm">{customProducts?.totalItems || 0}</span>
            <span className="text-primary-500 text-[10px] ml-1 uppercase font-semibold">Total Requests</span>
          </div>
        </div>
      </div>

      <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {customProducts?.requests?.length > 0 ? (
          customProducts.requests.map((item) => (
            <div key={item.id} className={`bg-white rounded-2xl shadow-sm border ${item.isDiscontinued ? 'opacity-70 grayscale border-red-100' : 'border-gray-100'} hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col relative`}>
              {/* Card Header */}
              <div className="p-4 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
                <span className="bg-primary-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                  {item.CPID}
                </span>
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase border ${item.inventoryRequest ? 'bg-red-50 text-red-600 border-red-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                  {item.inventoryRequest ? 'Stock Requested' : 'Listing Only'}
                </span>
              </div>

              {/* Card Body */}
              <div className="p-5 flex-1">
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="bg-gray-100 text-gray-600 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">
                      {item.type || 'Other'}
                    </span>
                    <div className="flex items-center gap-1 text-primary-600">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                      <span className="text-[10px] font-extrabold uppercase tracking-wider">{item.prescriptionCount || 0} Uses</span>
                    </div>
                  </div>
                  <h4 className="text-gray-900 font-bold text-lg uppercase leading-tight line-clamp-2 min-h-[3rem]">
                    {item.name}
                  </h4>
                  <p className="text-gray-500 text-sm italic mt-1 line-clamp-1">
                    {item.drug || 'No drugs specified'}
                  </p>
                </div>

                <div className="space-y-2 mt-4 pt-4 border-t border-gray-50">
                  <div className="flex justify-between items-center text-[11px]">
                    <span className="text-gray-400 uppercase font-semibold">Doctor</span>
                    <span className="text-gray-700 font-bold truncate max-w-[120px]">{item.doctorName || item.did}</span>
                  </div>
                  <div className="flex justify-between items-center text-[11px]">
                    <span className="text-gray-400 uppercase font-semibold">Store</span>
                    <span className="text-gray-700 font-bold truncate max-w-[120px]">{item.storeName || item.sid}</span>
                  </div>
                  <div className="flex justify-between items-center text-[11px]">
                    <span className="text-gray-400 uppercase font-semibold">Date Registered</span>
                    <span className="text-gray-700 font-medium">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {item.IID && (
                  <div className="mt-4 p-2.5 bg-success-50 rounded-xl border border-success-100 flex items-center justify-between">
                    <div>
                      <p className="text-[9px] text-success-600 font-bold uppercase">Linked Inventory ID</p>
                      <p className="text-sm text-success-700 font-extrabold">{item.IID}</p>
                    </div>
                    <div className="h-6 w-6 bg-success-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                    </div>
                  </div>
                )}
              </div>

              {/* Card Footer */}
              <div className="p-4 bg-gray-50/30 border-t border-gray-50 mt-auto flex gap-2">
                <button
                  onClick={() => handleToggleDiscontinue(item)}
                  title={item.isDiscontinued ? 'Activate Product' : 'Discontinue Product'}
                  className={`px-3 py-2.5 rounded-xl border transition-all duration-200 flex items-center justify-center ${item.isDiscontinued ? 'bg-success-50 border-success-200 text-success-600 hover:bg-success-100' : 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100'}`}
                >
                  {item.isDiscontinued ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  )}
                </button>
                <button
                  onClick={() => handleOpenModal(item)}
                  className={`flex-1 py-2.5 rounded-xl text-[11px] font-bold transition-all duration-200 ${item.IID ? 'bg-white border border-gray-200 text-gray-400 hover:bg-gray-50' : 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'}`}
                >
                  {item.IID ? 'Update IID' : 'Add to Inventory'}
                </button>
              </div>

              {item.isDiscontinued && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-12 pointer-events-none">
                  <span className="border-4 border-red-500/30 text-red-500/30 text-2xl font-black px-4 py-1 rounded uppercase tracking-widest bg-white/50 backdrop-blur-sm">
                    Blocked
                  </span>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="col-span-full py-20 text-center">
            <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 00-2 2H6a2 2 0 00-2 2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path></svg>
            </div>
            <h3 className="text-gray-500 font-bold">No custom products found</h3>
            <p className="text-gray-400 text-sm mt-1">New requests from doctors will appear here</p>
          </div>
        )}
      </div>

      <div className="p-6">
        <Pagination
          currentPage={currentPage}
          totalPages={customProducts?.totalPages || 1}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          isPreviousDisabled={currentPage === 1}
          isNextDisabled={currentPage >= (customProducts?.totalPages || 1)}
          totalItems={customProducts?.totalItems || 0}
          itemLength={customProducts?.requests?.length || 0}
        />
      </div>

      {/* IID Input Modal */}
      {showIIDModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-[90%] border border-white/20 transform animate-in fade-in zoom-in duration-200">
            <div className="text-center mb-6">
              <div className="bg-primary-50 w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path></svg>
              </div>
              <h3 className="text-gray-900 font-semibold text-lg">Link Inventory ID</h3>
              <p className="text-gray-500 text-[20px] mt-2 uppercase font-bold text-primary-600">Linking for: <span className="text-gray-900 underline">"{selectedRequest?.name}"</span></p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-[10px] font-bold uppercase mb-2 ml-1">
                  Enter Product IID
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={iidValue}
                    onChange={(e) => {
                      setIidValue(e.target.value);
                      setFetchedProduct(null);
                    }}
                    className="flex-1 border-2 border-gray-100 bg-gray-50 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 focus:bg-white transition-all uppercase font-bold"
                    placeholder="E.g. I000123"
                  />
                  <button
                    onClick={handleVerify}
                    disabled={!iidValue.trim() || isVerifying}
                    className="px-4 bg-primary-50 text-primary-600 rounded-xl font-bold text-[10px] uppercase hover:bg-primary-100 disabled:opacity-50 transition-colors"
                  >
                    {isVerifying ? '...' : 'Verify'}
                  </button>
                </div>
              </div>

              {fetchedProduct && (
                <div className="bg-success-50/50 border border-success-100 rounded-2xl p-4 animate-in slide-in-from-bottom-2 duration-300">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 bg-success-500 rounded-full"></span>
                    <span className="text-[10px] font-bold text-success-600 uppercase">Product Found</span>
                  </div>
                  <p className="text-sm font-black text-gray-900 uppercase">{fetchedProduct.productName}</p>
                  <div className="flex gap-2 mt-1">
                    <span className="text-[9px] bg-white px-1.5 py-0.5 rounded border border-success-100 text-success-700 font-bold uppercase">{fetchedProduct.type}</span>
                    <span className="text-[9px] bg-white px-1.5 py-0.5 rounded border border-success-100 text-success-700 font-bold uppercase">{fetchedProduct.manufacturer}</span>
                  </div>
                  <p className="text-[9px] text-success-700 italic mt-2">Please confirm this is the correct item to link.</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowIIDModal(false)}
                  className="flex-1 py-3 text-gray-500 font-bold text-xs rounded-xl hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveIID}
                  disabled={!iidValue.trim() || !fetchedProduct}
                  className="flex-1 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-200 text-white font-bold text-xs rounded-xl transition-all"
                >
                  Link & Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Requests;
